<template>
  <div class="flex flex-col h-full bg-white divide-y divide-gray-200 shadow-xl">
    <div class="flex flex-col flex-1 min-h-0 py-6 overflow-y-scroll">
      <div class="px-4 sm:px-6">
        <div class="flex items-start justify-between">
          <DialogTitle class="text-base font-semibold leading-6 text-gray-900">
            {{ translatedTitle }}
          </DialogTitle>
          <div class="flex items-center ml-3 h-7">
            <button
              type="button"
              class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="$emit('cancel')">
              <span class="sr-only">{{ $t("Close panel") }}</span>
              <X class="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <div class="relative flex-1 px-4 mt-6 sm:px-6">
        <TextInput
          v-model="query"
          label=""
          name="name"
          type="text"
          :validate-on-input="true"
          classes="w-full p-2 "
          :placeholder="$t('Search team members by email or name')" />
        <div class="pt-2">
          <div v-if="filtered_users.length">
            <ul role="list" class="flex-1 divide-y divide-gray-200 dark:divide-gray-500">
              <li v-for="user in filtered_users" :key="user.email" class="cursor-pointer">
                <div class="relative flex items-center px-5 pt-6 group">
                  <div class="flex-1 block p-1 -m-1">
                    <div class="absolute inset-0 group-hover:bg-gray-100 dark:group-hover:bg-dark-divider" aria-hidden="true" />
                    <div class="relative flex items-center flex-1 min-w-0">
                      <span class="relative flex-shrink-0 inline-block">
                        <img v-if="user.avatar" class="inline-block w-10 h-10 rounded-full" :src="user.avatar" alt="">
                        <Avatar v-else :first-name="user.first_name" :last-name="user.last_name" />
                      </span>
                      <div class="ml-4 truncate">
                        <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
                          {{ Record.from(user).full_name }}
                        </p>
                        <p class="text-sm text-gray-500 truncate">
                          {{ Record.from(user).email }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div as="div" class="relative flex-shrink-0 inline-block ml-2 text-left">
                    <slot name="actions">
                      <Switch
                        :checked="user.is_checked"
                        :disabled="user.is_disabled"
                        :class="[
                          { 'opacity-60': user.is_disabled },
                          user.is_checked ? 'bg-blue' : 'bg-gray-200 dark:bg-gray-400',
                          'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                        ]"
                        @click.stop="toggleUser(user)">
                        <span
                          aria-hidden="true"
                          :class="[
                            user.is_checked && $store.state.portfolio.ui_options?.language == 'ar'
                              ? '-translate-x-5'
                              : user.is_checked
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                          ]" />
                      </Switch>
                    </slot>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div v-else>
            <h3 class="flex justify-center text-sm">
              {{ $t("No results") }}
            </h3>
          </div>
        </div>
      </div>
      <div v-if="isAllRecords" class="flex items-center space-x-2 ml-2 text-status-error-red mb-4">
        {{ $t("Confirm assigning to") }} {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table") }}

        <input
          v-model="confirm_assign_all"
          type="checkbox"
          name="truncate"
          class="ml-2 form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
      </div>
    </div>

    <div class="flex justify-end flex-shrink-0 px-4 py-4">
      <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
      <button :disabled="isAllRecords && !confirm_assign_all" type="submit" class="w-1/2 primary-btn" @click="submit">
        {{ translatedSubmitText }}
      </button>
    </div>
  </div>
</template>

<script>
import { DialogTitle, Switch } from "@headlessui/vue"
import Avatar from "./Avatar.vue"
import TextInput from "./TextInput.vue"
import { Record } from "@/models/record"
import { X } from "lucide-vue-next"
import { utils } from "@/store/helpers"

export default {
  components: {
    DialogTitle,
    X,
    Avatar,
    Switch,
    TextInput
  },
  props: {
    count: null,
    title: {
      type: String,
      default: "Change Co-Owners"
    },
    submitText: {
      type: String,
      default: "Submit"
    },
    selectedUsers: {
      type: Array,
      default: () => []
    },
    excludedUsers: {
      type: Array,
      default: () => []
    },
    disabledUsers: {
      type: Array,
      default: () => []
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    }
  },
  emits: ["cancel", "submit", "update:deselectedUsers", "update:selectedUsers", "close"],
  data() {
    return {
      Record,
      query: null,
      users: [],
      assigned_users: [],
      unassigned_users: [],
      confirm_assign_all: false
    }
  },
  computed: {
    translatedTitle() {
      return this.$t(this.title)
    },
    translatedSubmitText() {
      return this.$t(this.submitText)
    },
    filtered_users() {
      if (this.query) {
        return this.users.filter(
          (u) => u["email"].toLowerCase().search(this.query) != -1 || u["full_name"].toLowerCase().search(this.query) != -1
        )
      } else {
        return this.users
      }
    }
  },

  created() {
    this.users = this.$store.state.data.users
      .filter((u) => this.excludedUsers.filter((uu) => uu.id == u.id).length == 0)
      .map((u) => {
        u["is_checked"] = this.selectedUsers.findIndex((x) => x == u.id || x?.id == u.id) != -1
        u["is_disabled"] = this.disabledUsers.findIndex((x) => x == u.id || x?.id == u.id) != -1
        return u
      })
    this.assigned_users = this.users.filter((u) => u.is_checked)
    this.$emit("update:selectedUsers", this.assigned_users)
  },

  methods: {
    toggleUser(user) {
      user.is_checked = !user.is_checked
      if (user.is_checked) {
        this.unassigned_users = this.unassigned_users.filter((u) => u.id != user.id)
        utils.replaceOrAddItem(this.assigned_users, user)
      } else {
        this.assigned_users = this.assigned_users.filter((u) => u.id != user.id)
        utils.replaceOrAddItem(this.unassigned_users, user)
      }

      this.$emit("update:selectedUsers", this.assigned_users)
      this.$emit("update:deselectedUsers", this.unassigned_users)
    },
    submit() {
      this.$emit(
        "submit",
        this.users.filter((u) => u.is_checked)
      )
    }
  }
}
</script>
